import React from 'react';
import { Card, CardHeader, Alert, CardBody, Table, Collapse, Row, Col } from 'reactstrap';
import * as utils from '../../Services/utils';
import { InfoTag } from '../shared/infoTag';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const JobCompetencesAndQuestionsCard = ({
  isOpen,
  toggleAccordion,
  jobCompetences,
  jobQuestions,
  createJobCompetence,
  editJobCompetence,
  deleteJobCompetence,
  editJobQuestion,
  deleteJobQuestion,
  maxPossibleScore,
  createJobQuestion,
  onDragQuestionEnd,
}) => {
  const { t, i18n } = useTranslation();

  const createCompetenceWithValidations = (event) => {
    event.stopPropagation();
    createJobCompetence(event);
  };

  const createJobQuestionWithValidations = (event) => {
    event.stopPropagation();
    createJobQuestion(event);
  };

  return (
    <Row className="justify-content-center mt-3">
      <Col md={10}>
        <Card id="jobCompetencesAndQuestionsCard">
          <CardHeader style={{ cursor: 'pointer' }} onClick={toggleAccordion}>
            <div className="d-flex justify-content-between">
              <div>
                <h3>
                  <small>
                    <em className={isOpen ? 'fa fa-minus text-primary mr-2' : 'fa fa-plus text-primary mr-2'}></em>
                  </small>
                  {t('jobs.JOB_COMPETENCES')} &nbsp;
                  {jobCompetences?.length + jobQuestions?.length}{' '}
                  <small className="text-muted">
                    <InfoTag id="instructionForCompetences" lng={i18n.language}></InfoTag>
                  </small>
                </h3>
                {t('jobs.MAX_POSSIBLE_SCORE')}: {maxPossibleScore}{' '}
                <small className="text-muted">
                  <InfoTag id="maxPossibleScoreDescription" lng={i18n.language}></InfoTag>
                </small>
              </div>
            </div>
          </CardHeader>
          <Collapse isOpen={isOpen}>
            <CardBody>
              <Card className="card-default mb-1">
                <CardBody className="border-top">
                  <div className="d-flex justify-content-between align-items-center pb-3">
                    <h3 className="text-center mb-0">{t('competences.COMPETENCES')}</h3>
                  </div>
                  {jobCompetences?.length > 0 ? (
                    <div style={{ overflowX: 'auto', width: '100%' }}>
                      <Table striped bordered style={{ minWidth: '800px' }}>
                        <thead>
                          <tr>
                            <th hidden>ID</th>
                            <th>{t('competences.NAME')}</th>
                            <th style={{ width: '90px' }}>{t('common.TYPE')}</th>
                            <th style={{ width: '90px' }}>
                              {t('competences.WEIGHT')}&nbsp;
                              <InfoTag id="weightDescription2" lng={i18n.language}></InfoTag>
                            </th>
                            <th>{t('common.DESCRIPTION')}</th>
                            <th style={{ width: '100px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobCompetences?.map((item, index) => (
                            <tr key={item.competenceId}>
                              <td hidden>{item.competenceId}</td>
                              <td>{item.competenceName}</td>
                              <td>
                                <div className={utils.getBadgeColor(item.type)}>
                                  {utils.translateCompetenceType(item.type)}
                                </div>
                              </td>
                              <td>
                                <em className="fa-x mr-2 fas fa-bolt"> {item.weight}</em>
                              </td>
                              <td>
                                <p
                                  dangerouslySetInnerHTML={{
                                    __html: item.description,
                                  }}
                                />
                              </td>
                              <td className="text-right">
                                <em
                                  id={`pencilButtonJobDetails-${index}`}
                                  style={{ cursor: 'pointer' }}
                                  className="fa-2x mr-2 fas icon-pencil"
                                  onClick={() => editJobCompetence(item, index)}
                                ></em>
                                <em
                                  id={`trashButtonJobDetails-${index}`}
                                  style={{ cursor: 'pointer' }}
                                  className="fa-2x mr-2 fas icon-trash"
                                  onClick={() => deleteJobCompetence(item)}
                                ></em>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert color="dark" className="m-0">
                      {t('competences.NO_COMPETENCES')}
                    </Alert>
                  )}
                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-primary mt-3" onClick={createCompetenceWithValidations}>
                      {t('jobs.ADD_COMPETENCE')}
                    </button>
                  </div>
                  <br />
                  <div className="d-flex justify-content-between align-items-center pb-3">
                    <h3 className="text-center mb-0">{t('questions.QUESTIONS')}</h3>
                  </div>
                  {jobQuestions?.length > 0 ? (
                    <DragDropContext onDragEnd={onDragQuestionEnd}>
                      <Droppable droppableId="jobQuestions">
                        {(provided) => (
                          // Wrap tbody in a div and assign ref here
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{ overflowX: 'auto', width: '100%' }}
                          >
                            <Table striped bordered style={{ minWidth: '800px' }}>
                              <thead>
                                <tr>
                                  <th style={{ width: '90%' }}>{t('questions.QUESTION')}</th>
                                  <th style={{ width: '10%' }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {jobQuestions.map((item, index) => (
                                  <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index}>
                                    {(provided) => (
                                      <tr
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        <td style={{ width: '90%' }}>{item.content}</td>
                                        <td className="text-right" style={{ width: '10%' }}>
                                          <em
                                            id={`pencilButtonJobDetailsQuestion-${index}`}
                                            style={{ cursor: 'pointer' }}
                                            className="fa-2x mr-2 fas icon-pencil"
                                            onClick={() => editJobQuestion(item)}
                                          ></em>
                                          <em
                                            id={`trashButtonJobDetailsQuestion-${index}`}
                                            style={{ cursor: 'pointer' }}
                                            className="fa-2x mr-2 fas icon-trash"
                                            onClick={() => deleteJobQuestion(item)}
                                          ></em>
                                        </td>
                                      </tr>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  ) : (
                    <Alert color="dark" className="m-0">
                      {t('questions.NO_QUESTIONS')}
                    </Alert>
                  )}

                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-primary mt-3" onClick={createJobQuestionWithValidations}>
                      {t('jobs.ADD_QUESTION')}
                    </button>
                  </div>
                </CardBody>
              </Card>
            </CardBody>
          </Collapse>
        </Card>
      </Col>
    </Row>
  );
};

export default JobCompetencesAndQuestionsCard;
